import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Activity, Ban, FileText, Globe, type LucideIcon } from 'lucide-react';
import {
  Badge,
  type BadgeVariant,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  PageHeader,
  Skeleton,
} from '@beamx/shared';
import { messages, type MessageStatus } from '../lib/api/messages.js';

const STATUS_VARIANT: Record<MessageStatus, BadgeVariant> = {
  Sent: 'success',
  Sending: 'default',
  Accepted: 'default',
  Failed: 'destructive',
};

const FEATURES: { to: string; title: string; description: string; icon: LucideIcon }[] = [
  { to: '/sender-domains', title: 'Sender domains', description: 'Authenticate domains with SPF, DKIM, DMARC.', icon: Globe },
  { to: '/templates', title: 'Templates', description: 'MJML or HTML with Handlebars-style variables.', icon: FileText },
  { to: '/activity', title: 'Activity', description: 'Every message you’ve sent, with timelines.', icon: Activity },
  { to: '/suppression', title: 'Suppression', description: 'Bounces, complaints, and unsubscribes.', icon: Ban },
];

export function DashboardPage() {
  const recent = useQuery({
    queryKey: ['messages', { take: 5, skip: 0 }],
    queryFn: () => messages.list({ take: 5, skip: 0 }),
  });

  return (
    <>
      <PageHeader title="Console" description="Send transactional and marketing email with full deliverability control." />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {FEATURES.map((f) => (
          <Link key={f.to} to={f.to} className="group">
            <Card className="h-full transition-colors group-hover:border-primary/40">
              <CardContent className="pt-6">
                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <f.icon className="h-5 w-5" />
                </div>
                <div className="font-medium text-foreground">{f.title}</div>
                <p className="mt-1 text-sm text-muted-foreground">{f.description}</p>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <Card className="mt-6">
        <CardHeader className="flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Recent messages</CardTitle>
            <CardDescription>Your five most recent sends.</CardDescription>
          </div>
          <Link to="/activity" className="text-sm font-medium text-primary hover:underline">
            View all
          </Link>
        </CardHeader>
        <CardContent>
          {recent.isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : recent.data && recent.data.messages.length > 0 ? (
            <ul className="divide-y">
              {recent.data.messages.map((m) => (
                <li key={m.id} className="flex items-center justify-between gap-3 py-2.5">
                  <div className="min-w-0">
                    <Link to={`/messages/${m.id}`} className="truncate font-medium text-foreground hover:text-primary">
                      {m.subject}
                    </Link>
                    <p className="truncate text-xs text-muted-foreground">{m.toEmail}</p>
                  </div>
                  <Badge variant={STATUS_VARIANT[m.status]} dot>{m.status}</Badge>
                </li>
              ))}
            </ul>
          ) : (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No messages yet. Send your first via the API or the Compose page.
            </p>
          )}
        </CardContent>
      </Card>
    </>
  );
}
